// components/SelectBuyPopUp.jsx
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { addToCart } from '../slices/cartSlice';
import { trackAddToCart } from '../utils/gaAnalytics';
import { useGetSubscriptionPlansQuery } from '../slices/subscriptionsApiSlice';
import { RENTAL_RATE_NON_ORG, RENTAL_RATE_ORG, SHIPPING_FEE } from '../constants/constants';

const SelectRentalPopUp = ({ product }) => {
  const [isPopUpVisible, setPopUpVisible] = useState(false);
  const [serviceOption, setServiceOption] = useState('rent');
  const [inStore, setInStore] = useState(true);
  const [useSubscription, setUseSubscription] = useState(false);
  const [rentalPeriod, setRentalPeriod] = useState('4days');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 認証/サブスク可否
  const { userInfo } = useSelector((state) => state.auth);
  const canUseSubscription =
    !!userInfo?.subscription &&
    userInfo.subscription.usedItems < userInfo.subscription.maxItems &&
    (Number(product?.price) || 0) <= (userInfo.subscription.itemPriceLimit ?? Infinity);

  // 料金計算
  const calcBasePrice = ({
    price = 0,
    serviceOption,
    useSubscription,
    canUseSubscription,
    isOrgUser,
    rentalPeriod,
  }) => {
    if (serviceOption !== 'rent') return price;                 // 購入
    if (useSubscription && canUseSubscription) return 0;        // サブスク枠使用
    const table = isOrgUser ? RENTAL_RATE_ORG : RENTAL_RATE_NON_ORG;
    const rate = table[rentalPeriod] ?? table.default ?? 1;
    return Math.round(price * rate);
  };

  const basePrice = useMemo(
    () =>
      calcBasePrice({
        price: Number(product?.price) || 0,
        serviceOption,
        useSubscription,
        canUseSubscription,
        isOrgUser: !!userInfo?.organization,
        rentalPeriod,
      }),
    [product?.price, serviceOption, useSubscription, canUseSubscription, userInfo?.organization, rentalPeriod]
  );

  const shippingCost = useMemo(() => (inStore ? 0 : SHIPPING_FEE), [inStore]);
  const finalCost = useMemo(() => basePrice + shippingCost, [basePrice, shippingCost]);

  // 在庫に応じて既定タブを変更
  useEffect(() => {
    if (product && product.status !== 'available') setServiceOption('buy');
  }, [product]);

  // モーダル表示時は背景スクロールを止める + Escで閉じる
  useEffect(() => {
    if (!isPopUpVisible) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && setPopUpVisible(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isPopUpVisible]);

  const openPopUp = () => {
    setServiceOption('rent');
    setRentalPeriod('4days');
    setPopUpVisible(true);
  };
  const closePopUp = () => setPopUpVisible(false);

  // カート投入
  const addToCartHandler = () => {
    if (!product) return;
    if (!useSubscription && (rentalPeriod === 'default' || !rentalPeriod)) {
      toast.info('レンタル期間を選択してください');
      return;
    }
    const itemPayload = {
      ...product,
      serviceOption,
      inStore,
      useSubscription: canUseSubscription && useSubscription,
      rentalPeriod: !useSubscription ? rentalPeriod : 'unlimited',
      finalPrice: finalCost,
      shippingCost,
    };
    dispatch(addToCart(itemPayload));
    trackAddToCart(itemPayload);
    navigate('/cart');
  };

  // プラン取得（件数は使わない＝未加入誘導だけに使う）
  const { isLoading, isError, error } = useGetSubscriptionPlansQuery();
  if (isLoading) return <div>Loading...</div>;
  if (isError)   return <div>Error: {error?.message || 'failed to load plans'}</div>;

  return (
    <div className="select-rental-popup">
      <button onClick={openPopUp} className="select-rental-popup-button">
        レンタルする
      </button>

      {isPopUpVisible && serviceOption === 'rent' && (
        <div
          className="select-rental-popup-overlay"
          onClick={closePopUp}
          aria-hidden="true"
        >
          <div
            className="select-rental-popup-modal"
            role="dialog"
            aria-modal="true"
            aria-label="レンタルオプション"
            onClick={(e) => e.stopPropagation()} // 中身クリックで閉じない
          >
            <div className="select-rental-popup-modal-headmargin" />

            <p className="select-rental-popup-modal-title">1.借りる期間をお選びください</p>

            <div className="select-rental-popup-modal-selecttime">
              <div className="select-rental-popup-modal-selecttime-time">
                <Form.Check
                  type="radio"
                  label="3泊4日 - 定価の40％"
                  name="rentalPeriod"
                  value="4days"
                  checked={rentalPeriod === '4days' && !useSubscription}
                  disabled={useSubscription}
                  onChange={(e) => { setUseSubscription(false); setRentalPeriod(e.target.value); }}
                />
                <Form.Check
                  type="radio"
                  label="2週間 - 定価の50％"
                  name="rentalPeriod"
                  value="2weeks"
                  checked={rentalPeriod === '2weeks' && !useSubscription}
                  disabled={useSubscription}
                  onChange={(e) => { setUseSubscription(false); setRentalPeriod(e.target.value); }}
                />
                <Form.Check
                  type="radio"
                  label="1ヶ月 - 定価の60％"
                  name="rentalPeriod"
                  value="1month"
                  checked={rentalPeriod === '1month' && !useSubscription}
                  disabled={useSubscription}
                  onChange={(e) => { setUseSubscription(false); setRentalPeriod(e.target.value); }}
                />
              </div>

              <p className="select-rental-popup-modal-selecttime-text">or</p>

              <div className="select-rental-popup-modal-selecttime-subscription">
                <Form.Check
                  id="radio-use-subscription"
                  type="radio"
                  name="rentalPlan"
                  label="サブスク枠を利用する"
                  checked={serviceOption === 'rent' && useSubscription === true}
                  disabled={!canUseSubscription}
                  onChange={() => {
                    if (!canUseSubscription) {
                      toast.info('サブスク未加入のため選択できません。サブスクプランをご確認ください。');
                      return;
                    }
                    setServiceOption('rent');
                    setUseSubscription(true);
                    setRentalPeriod('1month');
                  }}
                />

                {!canUseSubscription && (
                  <a
                    className="select-rental-popup-modal-selecttime-subscription-nonuser"
                    href="/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open('/subscriptions', '_blank', 'noopener,noreferrer');
                      e.preventDefault();
                    }}
                  >
                    ➨ プランを見る <FaExternalLinkAlt size={12} style={{ marginLeft: '4px' }} />
                  </a>
                )}
              </div>
            </div>

            <p className="select-rental-popup-modal-title">2.受取方法をお選びください</p>
            <div className="select-rental-popup-modal-reserveoption">
              <Form.Check
                type="checkbox"
                label={`店舗で受け取る（${product?.shopArea?.name ?? '店舗'}）`}
                checked={inStore}
                onChange={(e) => setInStore(e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="郵送で受け取る（送料は880円）"
                checked={!inStore}
                onChange={(e) => setInStore(!e.target.checked)}
              />
            </div>

            <p className="select-rental-popup-modal-warning">
              ※レンタル開始日は、店頭受取はお客様が商品を受け取った日、郵送受取は店舗が商品を発送した日となります。<br />
              ※返却完了日は、店舗が商品を受け取った日となります。郵送返却の場合は早めの返却を推奨しております。
            </p>

            <div className="select-rental-popup-modal-price">
              <p className="select-rental-popup-modal-price-lable">小計：</p>
              <p className="select-rental-popup-modal-price-cost">
                ¥{finalCost.toLocaleString()}
              </p>
            </div>

            <div className="select-rental-popup-modal-actions">
              <button
                className="select-rental-popup-modal-actions-back"
                onClick={closePopUp}
              >
                商品ページに戻る
              </button>
              <button
                className="select-rental-popup-modal-actions-cart"
                onClick={addToCartHandler}
              >
                カートに入れる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectRentalPopUp;

